import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHotkeys, type HotkeyMap } from '@/hooks/use-hotkeys';
import { useEntitlements } from '@/lib/entitlements';
import { MODULES, type ModuleId } from '@/lib/modules';
import { GOTO_SHORTCUTS, isNavigable } from './nav-targets';
import { useShell } from './shell-context';

/**
 * The global half of the keyboard contract (ARCHITECTURE §9). The `g` jumps are
 * built from the enabled modules, so a shortcut never lands on a screen the
 * client did not buy — nor on one that does not exist yet, which is why `g t`
 * stays dark until Tareas ships its own view.
 */
export function GlobalHotkeys() {
  const { toggleCollapsed, setPaletteOpen, setShortcutsOpen } = useShell();
  const { showsInNav } = useEntitlements();
  const navigate = useNavigate();

  const hotkeys = useMemo<HotkeyMap>(() => {
    const map: HotkeyMap = {
      'mod+k': () => setPaletteOpen(true),
      'mod+b': () => toggleCollapsed(),
      '?': () => setShortcutsOpen(true),
    };

    for (const [id, binding] of Object.entries(GOTO_SHORTCUTS)) {
      const moduleId = id as ModuleId;
      // `showsInNav` y no `hasModule`: un atajo es un destino del cascarón igual
      // que el menú, y dejarlo vivo tras esconder la pantalla es la misma
      // pantalla ofrecida por la puerta de atrás.
      if (!binding || !showsInNav(moduleId) || !isNavigable(moduleId)) continue;
      map[binding] = () => navigate(MODULES[moduleId].path);
    }

    return map;
  }, [showsInNav, navigate, setPaletteOpen, setShortcutsOpen, toggleCollapsed]);

  useHotkeys(hotkeys);
  return null;
}
